import React from 'react'
import { formatCurrency } from 'utils/currencyFormatter'

// The welcome coupon offer on the public team page, where the student compares
// prices: `card` sits at the top of the desktop price card, `bar` sits above the
// mobile bottom bar's buttons. Both get the same `offer` from
// useWelcomeCouponOffer, which the page calls once.
//
// The amber is fixed on purpose. In the team colour the old pill blended into
// the header and the page's own buttons, and visitors read it as navigation.
//
// `trialPrice` is { original, final, currency } from welcomeCouponTrialPrice, or
// null when there is no paid trial to name; then only the coupon is named.
// A held coupon is the top pill's job (WelcomeCouponBanner), so it renders
// nothing here.
const AMBER = { background: '#FAEEDA', border: '#EF9F27', text: '#633806', subtext: '#854F0B', button: '#BA7517' }

const GiftIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
  </svg>
)

const fill = (template, values) =>
  Object.entries(values).reduce((text, [key, value]) => text.replace(`%{${key}}`, value), template || '')

export default function WelcomeCouponOffer({ variant = 'card', offer, amount, currency = 'TWD', loggedIn, copy, trialPrice }) {
  const status = offer?.status
  if (status !== 'claimable' && status !== 'claimed') return null

  const isBar = variant === 'bar'
  const placement = isBar ? 'mobile_bar' : 'price_card'

  if (status === 'claimed') {
    const label = fill(copy?.claimed, { date: offer.coupon?.expiresOn || '' })
    if (!label.trim()) return null
    return (
      <div
        role="status"
        className={isBar ? 'flex items-center gap-2 px-4 py-2 min-h-11 text-xs border-b' : 'flex items-center gap-2 rounded-lg border px-3 py-2.5 mb-4 text-sm'}
        style={{ backgroundColor: AMBER.background, borderColor: AMBER.border, color: AMBER.text }}
      >
        <GiftIcon className="w-4 h-4 flex-shrink-0" />
        <span className="min-w-0">{label}</span>
      </div>
    )
  }

  const values = {
    amount: formatCurrency(amount, currency),
    price: trialPrice ? formatCurrency(trialPrice.final, trialPrice.currency) : ''
  }
  const title = fill(loggedIn ? copy?.offerTitleClaim : copy?.offerTitleSignup, values)

  if (isBar) {
    let template = loggedIn ? copy?.offerTitleClaim : copy?.offerTitleSignup
    if (trialPrice && trialPrice.final === 0) {
      template = loggedIn ? copy?.offerMobileClaimFree : copy?.offerMobileSignupFree
    } else if (trialPrice) {
      template = loggedIn ? copy?.offerMobileClaim : copy?.offerMobileSignup
    }
    const label = fill(template, values)
    if (!label.trim()) return null
    return (
      <button
        type="button"
        onClick={() => offer.claim(placement)}
        className="w-full flex items-center gap-2 px-4 py-2 min-h-11 text-xs text-left border-b cursor-pointer"
        style={{ backgroundColor: AMBER.background, borderColor: AMBER.border, color: AMBER.text }}
      >
        <GiftIcon className="w-4 h-4 flex-shrink-0" />
        <span className="flex-1 min-w-0">{label}</span>
        <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </button>
    )
  }

  let trialLine = ''
  if (trialPrice) {
    trialLine = trialPrice.final === 0 ? (copy?.offerTrialFree || '') : fill(copy?.offerTrialPrice, values)
  }
  const cta = loggedIn ? copy?.offerCtaClaim : copy?.offerCtaSignup
  if (!title.trim() || !cta?.trim()) return null

  return (
    <div
      className="rounded-lg border px-3 py-3 mb-4"
      style={{ backgroundColor: AMBER.background, borderColor: AMBER.border }}
    >
      <p className="flex items-center gap-1.5 text-sm font-semibold" style={{ color: AMBER.text }}>
        <GiftIcon className="w-4 h-4 flex-shrink-0" />
        {title}
      </p>
      {trialLine && (
        <p className="text-xs mt-0.5" style={{ color: AMBER.subtext }}>{trialLine}</p>
      )}
      <button
        type="button"
        onClick={() => offer.claim(placement)}
        className="w-full mt-2.5 py-2 rounded-lg text-sm font-semibold text-white cursor-pointer hover:opacity-90 transition-opacity"
        style={{ backgroundColor: AMBER.button }}
      >
        {cta}
      </button>
    </div>
  )
}
